<script lang="ts">
  import FeatureFlag from '@/platform-redesign-components/FeatureFlag.svelte';
  import {project, ProjectState, subtitles} from '@/store';
  import {createEventDispatcher, onDestroy, onMount} from 'svelte';
  import {getRandomArbitrary, toTime} from '@/utils/common';
  import type {Video} from '@/types/project';
  import Icon from '../icon/Icon.svelte';
  import type {Unsubscriber} from 'svelte/store';
  import {Button} from '..';

  const dispatch = createEventDispatcher();
  let eventSource: EventSource;
  let currentProgress = 0;
  let oldProgress = 0;
  const PROGRESS_COMPLETE = 100;

  let video: Video | null = null;
  let estimatedTime = 0;
  let subscription: Unsubscriber;

  const onVideoChange = async (projectVideo: Video) => {
    const {transcription} = projectVideo;
    if (!transcription) return;
    // if transcription is null, and there is a valid operation id, then start listening to event status
    if (transcription.transcription === null && transcription.operationId !== null) {
      eventSource = await subtitles.getTranscriptionStatus(transcription.operationId);
      eventSource.onmessage = onStatusResponse;
    } else if (transcription.transcription !== null) {
      currentProgress = PROGRESS_COMPLETE;
    }
  };

  const onStatusResponse = async ({data}: any) => {
    const metadata = JSON.parse(data);

    if (metadata.progressPercent) {
      const {progressPercent} = metadata;

      if (metadata.progressPercent !== PROGRESS_COMPLETE) {
        if (oldProgress === progressPercent) {
          let cP = currentProgress + currentProgress * 0.02;
          currentProgress = cP < 100 ? cP : 99;
        } else {
          if (progressPercent > currentProgress) {
            currentProgress = progressPercent;
            oldProgress = progressPercent;
          }
        }
      } else {
        eventSource.close();
        project.getVideo();
      }
    } else {
      if (currentProgress === 0) {
        // initial percentage
        currentProgress = 5;
      } else if (currentProgress >= 30) {
        // stop incrementing at 30%
        currentProgress = currentProgress;
      } else {
        // add incremental values
        // Just for fun! Random incremental values
        const rand = getRandomArbitrary(0.3, 1.5);
        if (currentProgress < 80) currentProgress = currentProgress + rand;
        if (rand < 0.5) {
          let timeout: NodeJS.Timeout;
          timeout = setTimeout(() => {
            if (currentProgress < 80) currentProgress = currentProgress + getRandomArbitrary(1, 3);
            clearTimeout(timeout);
          }, 1000);
        }
      }
    }
  };

  onMount(() => {
    subscription = project.subscribe(_project => {
      const {video: projectVideo} = _project;
      video = projectVideo;
      if (video?.metadata?.duration) {
        estimatedTime = video?.metadata?.duration * 0.08;
      }

      if (!!video) {
        onVideoChange(projectVideo as Video);
      }
    });
  });

  // onDestroy, close event connection
  onDestroy(() => {
    subscription();
    if (eventSource) {
      eventSource.close();
    }
  });
</script>

<FeatureFlag>
  <div slot="new_component">
    {#if video !== null}
      <div class="flex h-12 w-96 bg-white">
        <div class="flex">
          {#if video.preview}
            <img src={video.preview} class="aspect-video h-full rounded object-contain" alt="" />
          {:else}
            <div class="flex h-full w-full items-center justify-center rounded bg-gray-200">
              <Icon name="Spinner" className=" text-gray-400  w-5 h-5 animate-spin" />
            </div>
          {/if}
          <div class="ml-4 flex flex-col">
            <div class="truncate text-xs font-bold" title={video.title}>
              {video.title || 'Loading...'}
            </div>

            <div class="mt-[6px] flex flex-row">
              <Button
                on:click={() => {
                  console.log('nice' /*technical debt*/);
                }}
                className="text-xs !py-1 px-2 text-center text-slate-700 font-bold"
                variant="secondary"
                text="Open Video Settings"
              />
              <div
                class="cursor-pointer px-2 py-1 text-xs font-semibold text-gray-700"
                on:click={() => dispatch('choose-different-video')}
                on:keydown={() => dispatch('choose-different-video')}
              >
                Choose Another Video
              </div>
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="flex flex-col">
        <div class="truncate text-xs font-bold">
          {'Loading...'}
        </div>
        <div class="ml-4 mt-[6px] flex flex-row">
          <Button
            on:click={() => {
              console.log('nice' /*technical debt*/);
            }}
            className="text-xs !py-1 px-2 text-center text-slate-700 font-bold"
            variant="secondary"
            text="Open Video Settings"
          />
          <div
            class="cursor-pointer px-2 py-1 text-xs font-semibold text-gray-700"
            on:click={() => dispatch('choose-different-video')}
            on:keydown={() => dispatch('choose-different-video')}
          >
            Choose Another Video
          </div>
        </div>
      </div>
    {/if}
  </div>
  <div slot="current_component">
    {#if video !== null}
      <div class="flex flex-col rounded-lg border-2 border-dashed border-gray-200 bg-white !p-4 !pb-2">
        <div class="!mb-4 flex gap-4">
          <div class=" relative flex h-full w-2/5 items-center rounded">
            {#if video.preview}
              <img src={video.preview} class="aspect-video h-full w-full rounded object-contain" alt="" />
            {:else}
              <div class="flex h-full w-full items-center justify-center rounded bg-gray-200">
                <Icon name="Spinner" className=" text-gray-400  w-5 h-5 animate-spin" />
              </div>
            {/if}
          </div>
          <div class="flex w-3/5 flex-col">
            <div class="truncate font-semibold" title={video.title}>
              {video.title || 'Loading...'}
            </div>
            <div class="text-sm">
              Duration: {toTime(video.metadata?.duration)}
            </div>
            <div
              class="mt-auto cursor-pointer text-sm italic text-red-500 underline"
              on:click={() => dispatch('choose-different-video')}
            >
              Choose a different video
            </div>
          </div>
        </div>
        <div class="relative h-2.5 w-full rounded-full">
          <div class="absolute h-2.5 w-full animate-pulse rounded-full bg-neutral bg-opacity-40" />
          <div
            class=" absolute z-50 h-2.5 rounded-full bg-neutral transition-all duration-500 ease-in-out"
            style="width: {currentProgress}%"
          />
        </div>
        <div class="mt-2 flex w-full items-center justify-between text-sm">
          {#if video.transcription?.transcription}
            {#if video.transcription.transcription.length == 0}
              <div>There was a problem with the transcription...</div>
            {:else}
              <div>Transcription Done</div>
              <div>{Math.round(currentProgress)}%</div>
            {/if}
          {:else}
            <Icon name="Spinner" className=" text-gray-400  w-5 h-5 animate-spin" />
            <div class="text-xs">
              Transcribing Video...
              {#if estimatedTime}
                estimated to take {toTime(estimatedTime)}
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {:else}
      <div class="flex w-1/3 flex-col rounded-lg border-2 border-dashed p-4">
        <div class="mb-1 flex-grow">Video Information</div>
        <div class="flex gap-4">
          <div class="relative w-1/3">
            <div class="h-full w-full animate-pulse bg-slate-400" />
          </div>
          <div class="flex w-2/3 flex-col gap-2">
            <div class="h-4 w-full animate-pulse bg-slate-400" />
            <div class="h-4 w-full animate-pulse bg-slate-400" />
            <div
              class="cursor-pointer italic text-red-500 underline"
              on:click={() => dispatch('choose-different-video')}
              on:keydown={() => dispatch('choose-different-video')}
            >
              Choose a different video
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>
</FeatureFlag>
